/**
 * Reusable member avatar component
 * Displays member photo if available, otherwise shows initials
 */

interface MemberAvatarProps {
  member: {
    memberId?: string;
    memberName: string;
    memberColor?: string;
    memberAvatar?: string;
  };
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const sizeClasses = {
  sm: {
    container: 'w-7 h-7',
    text: 'text-[10px]',
    border: 'border',
  },
  md: {
    container: 'w-9 h-9',
    text: 'text-xs',
    border: 'border',
  },
  lg: {
    container: 'w-10 h-10',
    text: 'text-sm',
    border: 'border-2',
  },
  xl: {
    container: 'w-14 h-14',
    text: 'text-base',
    border: 'border-2',
  },
};

export default function MemberAvatar({ member, size = 'md', className = '' }: MemberAvatarProps) {
  const sizeConfig = sizeClasses[size];
  const memberColor = member.memberColor || '#3b82f6';

  return (
    <div className={`relative ${className}`}>
      {/* Background circle with initials */}
      <div
        className={`${sizeConfig.container} rounded-full flex items-center justify-center text-white ${sizeConfig.text} font-bold`}
        style={{ backgroundColor: memberColor }}
      >
        {!member.memberAvatar && member.memberName.charAt(0).toUpperCase()}
      </div>
      {/* Avatar image overlay */}
      {member.memberAvatar && (
        <img
          src={member.memberAvatar}
          alt={member.memberName}
          className={`absolute inset-0 ${sizeConfig.container} rounded-full object-cover ${sizeConfig.border} border-white z-10`}
          style={{ borderColor: memberColor }}
          onError={(e) => {
            // Hide image on error, initials will show from background div
            (e.target as HTMLImageElement).style.display = 'none';
          }}
          onLoad={(e) => {
            // Ensure image is visible when loaded
            (e.target as HTMLImageElement).style.display = 'block';
          }}
        />
      )}
    </div>
  );
}
